package com.example.mystybag.login.logic

import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.biometricUtils.helpers.BiometricErrorResult
import com.example.mystybag.common.logic.NavigatorStaffBiometric
import com.example.mystybag.common.logic.NavigatorStaffChangePassword
import com.example.mystybag.common.logic.NavigatorStaffForgotPasswordEmail
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_VIEW_REVENUE
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.isValidEmail
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.login.model.StaffLoginUIState
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginStaffRequestModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class StaffLoginViewModel(
    context: AppContext
) : ChildViewModel<StaffLoginUIState>(context, StaffLoginUIState()) {

    private var autoBiometricTried: Boolean = false

    override fun onCreate() {
        navControls.backButtonVisible = true
        overrideBackButton = true
        navControls.title = ""

        val storedEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
        if (storedEmail.isNotEmpty() && uiState.value.email.isEmpty()) {
            updateState { it.copy(email = storedEmail) }
        }

        val biometricType = BiometricsHandler.getAuthenticationType()
        val biometricAvailable =
            biometricType != BiometricAuthenticationType.NONE && BiometricsHandler.isBiometricScannerAvailableAndSet()
        val useBiometricForStaff = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_STAFF, false)
        val hasSavedPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "").isNotEmpty()
        val showBiometricLogin =
            useBiometricForStaff && biometricAvailable && storedEmail.isNotEmpty() && hasSavedPassword

        updateState {
            it.copy(
                biometricType = biometricType,
                showBiometricLogin = showBiometricLogin
            )
        }

        if (showBiometricLogin && !autoBiometricTried) {
            autoBiometricTried = true
            onBiometricLoginClicked()
        }
    }

    fun onBiometricLoginClicked() {
        scope.launch {
            if (uiState.value.isLoading) return@launch

            val storedEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
            val hasSavedPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "").isNotEmpty()
            if (storedEmail.isEmpty() || !hasSavedPassword) {
                launchOnMain { showSnackbar(sharedString(SR.strings.please_log_in_again)) }
                return@launch
            }

            val biometricAvailable =
                uiState.value.biometricType != BiometricAuthenticationType.NONE &&
                    BiometricsHandler.isBiometricScannerAvailableAndSet()
            if (!biometricAvailable) {
                launchOnMain { showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later)) }
                return@launch
            }

            launchOnMain {
                val started = BiometricsHandler.startBiometricAuthentication(
                    activity = sharedActivity,
                    onSuccess = {
                        val storedPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "")
                        if (storedPassword.isEmpty()) {
                            showSnackbar(sharedString(SR.strings.please_log_in_again))
                        } else {
                            performLogin(email = storedEmail, password = storedPassword)
                        }
                    },
                    onFailed = { error ->
                        when (error) {
                            BiometricErrorResult.USER_CANCELED -> Unit
                            else -> showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                        }
                    }
                )
                if (!started) {
                    showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                }
            }
        }
    }

    fun onEmailChange(email: String) {
        updateState { it.copy(email = email, emailIsError = false, emailErrorMsg = "") }
    }

    fun onPasswordChange(password: String) {
        updateState { it.copy(password = password, passwordIsError = false, passwordErrorMsg = "") }
    }

    fun togglePasswordVisibility() {
        updateState { it.copy(passwordVisible = !it.passwordVisible) }
    }

    override fun onBackButtonClicked() {
        replaceAll(NavigatorWelcomeFromBack)
    }

    fun onForgotPasswordClicked() {
        navigate(NavigatorStaffForgotPasswordEmail)
    }

    fun onLoginClicked() {
        if (validate()) {
            performLogin(email = uiState.value.email, password = uiState.value.password)
        }
    }

    private fun validate(): Boolean {
        var isValid = true
        val state = uiState.value

        if (state.email.isEmpty() || !state.email.isValidEmail) {
            updateState {
                it.copy(
                    emailIsError = true,
                    emailErrorMsg = com.example.mystybag.core.expected.sharedString(com.example.mystybag.common.resources.SR.strings.please_enter_valid_email_address)
                )
            }
            isValid = false
        }

        if (state.password.isEmpty()) {
            updateState {
                it.copy(
                    passwordIsError = true,
                    passwordErrorMsg = com.example.mystybag.core.expected.sharedString(com.example.mystybag.common.resources.SR.strings.please_enter_valid_email_address)
                )
            }
            isValid = false
        }

        return isValid
    }

    private fun performLogin(email: String, password: String) {
        scope.launch {
            updateState { it.copy(isLoading = true) }

            com.example.mystybag.communication.Communication.token = ""
            Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
            Preferences.setLong(PREFERENCES_MERCHANT_ID, 0L)
            Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
            Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
            Preferences.setBoolean(PREFERENCES_STAFF_CAN_CREATE_BAGS, false)
            Preferences.setBoolean(PREFERENCES_STAFF_CAN_VIEW_REVENUE, false)

            commandBlock(showProgress = true) {
                CommonCommands.loginStaff(
                    LoginStaffRequestModel(email = email, password = Security.hashPasswordForRequest(password))
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }

                                val staffResponse = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.LoginStaffResponseModel
                                if (staffResponse != null) {
                                    com.example.mystybag.communication.Communication.token = staffResponse.jwt
                                    Preferences.setString(PREFERENCES_STAFF_EMAIL, email)
                                    Preferences.setString(PREFERENCES_MERCHANT_JWT, staffResponse.jwt)
                                    Preferences.setLong(PREFERENCES_MERCHANT_ID, staffResponse.merchantId)
                                    Preferences.setBoolean(PREFERENCES_IS_STAFF, true)
                                    Preferences.setString(
                                        PREFERENCES_STAFF_LOCATION_IDS,
                                        staffResponse.merchantLocationIds.joinToString(",")
                                    )
                                    Preferences.setBoolean(
                                        PREFERENCES_STAFF_CAN_CREATE_BAGS,
                                        staffResponse.canCreateBags
                                    )
                                    Preferences.setBoolean(
                                        PREFERENCES_STAFF_CAN_VIEW_REVENUE,
                                        staffResponse.canViewRevenue
                                    )

                                    CoroutineScope(Dispatchers.Default).launch {
                                        DatabaseProvider.clearDatabase()
                                    }

                                    val biometricAvailable =
                                        uiState.value.biometricType != BiometricAuthenticationType.NONE &&
                                            BiometricsHandler.isBiometricScannerAvailableAndSet()
                                    val useBiometricForStaff = Preferences.getBoolean(
                                        key = PREFERENCES_USE_BIOMETRIC_FOR_STAFF,
                                        defaultValue = false
                                    )
                                    val biometricPromptDeclined = Preferences.getBoolean(
                                        key = PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED,
                                        defaultValue = false
                                    )

                                    // Temporary password must be changed before main app — even when biometric setup would run first
                                    if (staffResponse.passwordIsTemporary) {
                                        navigate(NavigatorStaffChangePassword)
                                    } else if (!useBiometricForStaff && biometricAvailable && !biometricPromptDeclined) {
                                        Preferences.setString(PREFERENCES_MERCHANT_JWT, staffResponse.jwt)
                                        navigate(NavigatorStaffBiometric(
                                            passwordToSaveAfterBiometric = password,
                                            jwt = staffResponse.jwt,
                                            merchantId = staffResponse.merchantId,
                                            staffLocationIds = staffResponse.merchantLocationIds.joinToString(",")
                                        ))
                                    } else {
                                        setResult<LoginInterface> { goToMainScreen(false) }
                                    }
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }
}
